import { ItemsPerPage } from './multi-slide-carousel.types'
import { useState, useEffect } from 'react'
import {
  getBreakpointValue,
  MantineBreakpointsValues,
  MantineSize,
  useMantineTheme,
} from '@mantine/core'

type ItemsSizes = {
  [breakpoint: string]: string
}

export const formatToCssWidthValue = (width: number) => {
  const shortenedValue = width.toString().match(/^-?\d+(?:\.\d{0,2})?/)?.[0]
  return shortenedValue ? `${shortenedValue}%` : '100%'
}

export const getItemsSizes = (
  itemsPerPage: ItemsPerPage = { xs: 1 }
): ItemsSizes => {
  const breakpoints = Object.keys(itemsPerPage)
  const itemsAmount = Object.values(itemsPerPage)

  const itemsSizes: ItemsSizes = { base: '100%' }

  breakpoints.forEach((b, i) => {
    const sizeValue = formatToCssWidthValue(100 / itemsAmount[i])

    itemsSizes[b] = sizeValue
  })

  return itemsSizes
}

const breakpointOrder: MantineSize[] = ['xs', 'sm', 'md', 'lg', 'xl']

const getMantineBreakpointValues = (
  mantineBreakpoints: MantineBreakpointsValues
): Record<MantineSize, number> => {
  return breakpointOrder.reduce((breakpoint, size) => {
    breakpoint[size] = getBreakpointValue(size, mantineBreakpoints)
    return breakpoint
  }, {} as Record<MantineSize, number>)
}

const getSmallestBreakpoint = (
  itemsPerPage: ItemsPerPage
): MantineSize | null => {
  for (const breakpoint of breakpointOrder) {
    if (itemsPerPage[breakpoint] !== undefined) {
      return breakpoint
    }
  }
  return null
}

const getCurrentBreakpoint = (
  width: number,
  mantineBreakpointsValues: Record<MantineSize, number>
): MantineSize | null => {
  for (let i = breakpointOrder.length - 1; i >= 0; i--) {
    const breakpoint = breakpointOrder[i]
    if (width >= mantineBreakpointsValues[breakpoint]) {
      return breakpoint
    }
  }
  return null
}

const getItemsToScrollForWidth = (
  width: number,
  itemsPerPage: ItemsPerPage,
  mantineBreakpointsValues: Record<MantineSize, number>
): number => {
  const smallestBreakpoint = getSmallestBreakpoint(itemsPerPage)

  if (
    smallestBreakpoint &&
    width < mantineBreakpointsValues[smallestBreakpoint]
  ) {
    return 1
  }

  const breakpoint = getCurrentBreakpoint(width, mantineBreakpointsValues)
  if (breakpoint) {
    const value = itemsPerPage[breakpoint]
    if (value !== undefined) {
      return value
    }
  }

  for (const bp of breakpointOrder) {
    const value = itemsPerPage[bp]
    if (value !== undefined) {
      return value
    }
  }

  return 1
}

export const useGetItemsToScroll = (
  itemsPerPage: ItemsPerPage = { xs: 1 }
): number => {
  const { breakpoints } = useMantineTheme()
  const mantineBreakpointsValues = getMantineBreakpointValues(breakpoints)

  const [itemsToScroll, setItemsToScroll] = useState<number>(() => {
    if (typeof window === 'undefined') return 1
    return getItemsToScrollForWidth(
      window.innerWidth,
      itemsPerPage,
      mantineBreakpointsValues
    )
  })

  useEffect(() => {
    const handleResize = () => {
      const newItemsToScroll = getItemsToScrollForWidth(
        window.innerWidth,
        itemsPerPage,
        mantineBreakpointsValues
      )
      setItemsToScroll(newItemsToScroll)
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [itemsPerPage, mantineBreakpointsValues])

  return itemsToScroll
}
